import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { InvitationHttpService } from '../../core/state/invitation/invitation-http.service';
import { FormControlNameRecord } from '../../shared/data-types/form-types';
import { MustMatch } from '../../shared/validators/must-match.validator';

@Component({
  selector: 'app-invitation-registration',
  templateUrl: './invitation-registration.component.html',
  standalone: false,
})
export class InvitationRegistrationComponent implements OnInit, OnDestroy {
  registerForm = signal<FormGroup | null>(null);

  // Forms
  public formControlNames: FormControlNameRecord = {
    FirstName: 'firstName',
    LastName: 'lastName',
    Email: 'email',
    Password: 'password',
    PasswordRepeat: 'passwordRepeat',
  };

  // DI
  private readonly formBuilder = inject(FormBuilder);
  private readonly invitationHttpService = inject(InvitationHttpService);

  // Misc
  private mustMatchEnabled = false;
  private emailValidatorEnabled = false;
  private passwordMinLengthEnabled = false;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly subs = new SubSink();

  ngOnInit(): void {
    this.buildForm();

    const token = this.route.snapshot.queryParamMap.get('token');
    this.invitationHttpService
      .resolveInvitation(token)
      .pipe(take(1))
      .subscribe((invitation) => {
        this.registerForm().controls[this.formControlNames.Email].setValue(invitation.email);
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public register(): void {
    // Enable email format validation only after first submit
    if (!this.emailValidatorEnabled) {
      const emailCtrl = this.registerForm().get(this.formControlNames.Email);
      emailCtrl.setValidators([Validators.required, Validators.email]);
      emailCtrl.updateValueAndValidity({ emitEvent: false });
      this.emailValidatorEnabled = true;
    }

    // Enable mustMatch only after first submit
    if (!this.mustMatchEnabled) {
      this.registerForm().setValidators(
        MustMatch(this.formControlNames.Password, this.formControlNames.PasswordRepeat),
      );
      this.registerForm().updateValueAndValidity({ emitEvent: false });
      this.mustMatchEnabled = true;
    }

    // Enable minLength only after first submit
    if (!this.passwordMinLengthEnabled) {
      const passwordCtrl = this.registerForm().get(this.formControlNames.Password);
      passwordCtrl.setValidators([Validators.required, Validators.minLength(8)]);
      passwordCtrl.updateValueAndValidity({ emitEvent: false });
      this.passwordMinLengthEnabled = true;
    }

    this.registerForm().markAllAsTouched();

    if (!this.registerForm().valid) {
      return;
    }

    const firstName = this.registerForm().controls[this.formControlNames.FirstName].value;
    const lastName = this.registerForm().controls[this.formControlNames.LastName].value;
    const password = this.registerForm().controls[this.formControlNames.Password].value;
    const passwordRepeat = this.registerForm().controls[this.formControlNames.PasswordRepeat].value;
    const token = this.route.snapshot.queryParamMap.get('token');

    this.invitationHttpService
      .acceptInvitation(firstName, lastName, password, passwordRepeat, token)
      .pipe(take(1))
      .subscribe(() => {
        void this.router.navigate(['/']);
      });
  }

  private buildForm(): void {
    this.registerForm.set(
      this.formBuilder.group({
        [this.formControlNames.FirstName]: ['', Validators.required],
        [this.formControlNames.LastName]: ['', Validators.required],
        [this.formControlNames.Email]: ['', Validators.required],
        [this.formControlNames.Password]: ['', Validators.required],
        [this.formControlNames.PasswordRepeat]: ['', Validators.required],
      }),
    );
  }
}
